import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check, CalendarClock, ShieldCheck, Wrench, FileText } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { brand, lifecyclePlan, inspection } from "../data/site";

const includedIcons = [CalendarClock, ShieldCheck, Wrench, FileText];

// The Lifecycle Plan is the one product nobody else in the Valley sells, so it
// gets a page of its own rather than a row in a services list.
//
// Pricing is deliberately absent until lifecyclePlan.showPricing is true — see
// the note on the data block in src/data/site.js. The page is written to read
// correctly either way: with pricing off, each tier shows its term and who it
// suits, and the call to action is an inspection rather than a checkout.
const LifecyclePlan = () => {
  const showPricing = lifecyclePlan.showPricing;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: lifecyclePlan.name,
    serviceType: "Roof maintenance plan",
    provider: { "@id": `${SITE_URL}/#business` },
    areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, Arizona" },
    description: lifecyclePlan.short,
  };

  return (
    <div>
      <SEO
        title={`${lifecyclePlan.name} | Roof Maintenance in Phoenix, AZ`}
        description="Scheduled roof inspections, priority storm-season service and minor repairs included — the Pepla Lifecycle Plan keeps small roof problems from becoming replacements."
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="section-dark text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/60 hover:text-brand text-sm mb-8 transition-colors">
            <ArrowRight size={14} className="rotate-180" /> All services
          </Link>
          <span className="kicker-dark">Only from Pepla</span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight mt-3 mb-6 max-w-4xl">
            {lifecyclePlan.name}
          </h1>
          <div className="brand-line mb-8" />
          <p className="text-white/80 text-xl leading-relaxed max-w-3xl mb-10">
            {lifecyclePlan.tagline}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#enroll" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
              Start With An Inspection <ArrowRight size={18} />
            </a>
            <a href={`tel:${brand.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/25 hover:border-brand hover:text-brand text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
              <Phone size={18} /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      {/* WHY IT EXISTS */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
              Why This Exists
            </h2>
            <div className="brand-line mb-8" />
            <p className="text-muted-foreground text-lg leading-relaxed">{lifecyclePlan.intro}</p>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">What You Get</h2>
            <div className="brand-line mb-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {lifecyclePlan.included.map((item, i) => {
              const Icon = includedIcons[i % includedIcons.length];
              return (
                <div key={item.title} className="bg-white/5 border border-white/10 rounded-2xl p-8">
                  <Icon size={30} className="text-brand mb-5" />
                  <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">{item.title}</h3>
                  <p className="text-white/65 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="kicker">How it works</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              It Starts On The Roof
            </h2>
            <div className="brand-line" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {lifecyclePlan.how.map((s) => (
              <div key={s.step} className="bg-card border border-ink/10 rounded-2xl p-8">
                <div className="font-heading text-5xl text-brand leading-none mb-5">{s.step}</div>
                <h3 className="font-heading text-xl uppercase tracking-wide mb-3">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TERMS */}
      <section className="py-24 bg-secondary border-y border-ink/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="kicker">Choosing a level</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Matched To The Roof You Have
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed">
              {showPricing
                ? "Three levels, billed annually. Which one fits depends on the roof — its size, its pitch, and how much life it has left — so we confirm it after the inspection."
                : "Three levels. Which one fits depends on the roof, and that only comes out of an inspection — so we confirm it after we have been on the roof, not before."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {lifecyclePlan.tiers.map((t) => (
              <div key={t.name} className="bg-card border border-ink/10 rounded-2xl p-6 flex flex-col">
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-1">{t.name}</h3>
                <div className="text-brand-dark font-semibold text-sm uppercase tracking-wide mb-4">{t.term}</div>
                {showPricing && t.price && (
                  <div className="font-heading text-4xl mb-4 leading-none">
                    {t.price}
                    <span className="text-muted-foreground text-base font-sans font-normal">
                      {t.period || "/year"}
                    </span>
                  </div>
                )}
                <p className="text-muted-foreground text-sm leading-relaxed flex-1">{t.best}</p>
              </div>
            ))}
          </div>

          {!showPricing && (
            <p className="text-muted-foreground text-sm leading-relaxed mt-8 max-w-3xl">
              Plan terms and pricing are confirmed in writing before anything is signed, the same
              way every Pepla estimate works. Nothing is charged until you have the inspection
              report and the plan terms in front of you.
            </p>
          )}
        </div>
      </section>

      {/* FAQS */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
              Straight Answers
            </h2>
            <div className="brand-line mb-10" />
            <div className="space-y-8">
              {lifecyclePlan.faqs.map((f) => (
                <div key={f.q} className="border-b border-ink/10 pb-8 last:border-0">
                  <h3 className="font-semibold text-lg mb-3">{f.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENROLL */}
      <section id="enroll" className="py-24 section-dark text-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <span className="kicker-dark">Get started</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-5">
              It Starts With An Inspection
            </h2>
            <div className="brand-line mb-7" />
            <p className="text-white/75 leading-relaxed mb-8">
              We will not enroll a roof we have not seen. Book the inspection — it is
              complimentary either way — and you will get the photo report and an honest
              answer about whether a plan makes sense for your roof at all.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Complimentary inspection with a photo report",
                inspection?.daylight || "Seven days a week, 7:00 AM to 7:00 PM",
                "No plan sold on a roof that needs repairs first",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-brand" />
                  </span>
                  <span className="text-white/80 text-sm leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <a href={`tel:${brand.phoneRaw}`} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
              <Phone size={18} /> {brand.phone}
            </a>
          </div>
          {/* No defaultService — the form's options come from the services
              list, which the Lifecycle Plan is deliberately not part of. The
              visitor picks what their roof actually needs. */}
          <LeadForm dark />
        </div>
      </section>
    </div>
  );
};

export default LifecyclePlan;
